import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table, Input, DatePicker, Select } from "antd";
import dayjs from "dayjs";
import commonFn from "@/utils/commonFn";

function FilterTextInput({ filterKey, value, onFilterChange, onFilterSubmit }) {
  const [innerValue, setInnerValue] = useState(value ?? "");

  // 外部筛选被清空/被别处修改时，同步一下
  useEffect(() => {
    setInnerValue(value ?? "");
  }, [value]);

  return (
    <Input
      allowClear
      value={innerValue}
      onChange={(e) => {
        const val = e.target.value;
        setInnerValue(val);
        onFilterChange?.(filterKey, val);
        // 仅清空（点击 X 或删空）时立即触发查询
        if (val === "") {
          onFilterSubmit?.({ [filterKey]: val });
        }
      }}
      onPressEnter={() => {
        onFilterSubmit?.({ [filterKey]: innerValue });
      }}
      style={{ marginTop: 4 }}
    />
  );
}

export default function EnhancedTable({
  columns = [],
  dataSource,
  loading,
  scroll,
  rowKey = "key",
  pagination = false,
  filters = null,
  onFilterChange,
  onFilterSubmit,
  rowSelection,
  onChange,
  size = "middle",
}) {
  const safeFilters = filters && typeof filters === "object" ? filters : {};

  // 用于自适应测量外层容器实际可用高度，避免依赖 calc(100vh - 固定px) 这种脆弱写法
  const wrapperRef = useRef(null);
  const [autoScrollY, setAutoScrollY] = useState(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const updateHeight = () => {
      const height = el.clientHeight;
      if (height > 0) setAutoScrollY(height);
    };

    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  
const resolvedScroll = useMemo(() => {
 
  if (scroll?.y !== undefined && scroll?.y !== null) return scroll;

  if (autoScrollY) return { ...scroll, y: autoScrollY };
  return scroll;
}, [scroll, autoScrollY]);

  const mergedColumns = useMemo(() => {
    const processedColumns =
      commonFn.handleColunmFn(columns).filter((col) => !col.hidden) || [];

    return processedColumns.map((col) => {
      if (!col.filterKey) return { ...col, align: col.align || "center" };

      if (col.filterType === "date") {
        return {
          ...col,
          align: col.align || "center",
          title: (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontWeight: 500 }}>{col.title}</div>
              <DatePicker
                allowClear
                format="MM/DD/YYYY"
                style={{ marginTop: 4, width: "100%" }}
                value={
                  safeFilters[col.filterKey]
                    ? dayjs(safeFilters[col.filterKey])
                    : null
                }
                onChange={(date) => {
                  const timestamp = date ? date.valueOf() : null;
                  onFilterChange?.(col.filterKey, timestamp);
                  onFilterSubmit?.({
                    ...safeFilters,
                    [col.filterKey]: timestamp,
                  });
                }}
              />
            </div>
          ),
        };
      }

      if (col.filterType === "select" && col.selectOptions?.length) {
        return {
          ...col,
          align: col.align || "center",
          title: (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontWeight: 500 }}>{col.title}</div>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const keyword = String(input ?? "").toLowerCase();
                  const text = String(
                    option?.label ?? option?.value ?? "",
                  ).toLowerCase();
                  return text.includes(keyword);
                }}
                placeholder={col.filterPlaceholder}
                style={{ marginTop: 4, width: "100%" }}
                value={safeFilters[col.filterKey] || undefined}
                options={col.selectOptions}
                onChange={(value) => {
                  onFilterChange?.(col.filterKey, value);
                  onFilterSubmit?.({
                    ...safeFilters,
                    [col.filterKey]: value,
                  });
                }}
              />
            </div>
          ),
        };
      }

      // 文本输入：改用独立的 FilterTextInput，打字过程不再触发查询/整表重渲染，
      // 只有按下回车（或点击清空）才会真正提交筛选条件
      return {
        ...col,
        align: col.align || "center",
        title: (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 500 }}>{col.title}</div>
            <FilterTextInput
              filterKey={col.filterKey}
              value={safeFilters[col.filterKey]}
              onFilterChange={onFilterChange}
              onFilterSubmit={(patch) =>
                onFilterSubmit?.({ ...safeFilters, ...patch })
              }
            />
          </div>
        ),
      };
    });
  }, [columns, safeFilters, onFilterChange, onFilterSubmit]);

  const handleTableChange = (paginationConfig, filters, sorter) => {
    onChange?.(paginationConfig, filters, sorter);
  };

  return (
    <div
      ref={wrapperRef}
      style={{ height: "100%", minHeight: 0, overflow: "hidden" }}
    >
      <Table
        rowClassName="table-row-gradient"
        rowKey={rowKey}
        loading={loading}
        columns={mergedColumns}
        dataSource={dataSource}
        pagination={pagination}
        rowSelection={rowSelection}
        scroll={resolvedScroll}
        onChange={handleTableChange}
        size={size}
      />
    </div>
  );
}
